import clsx from 'clsx';
import moment from 'moment';
import React, { useEffect } from 'react';

import { ShadowRoot } from '@/src/components/common/Elements/ShadowRoot/ShadowRoot';
import { DEFAULT_DATE_TIME_A_FORMAT } from '@/src/config';
import { parseAndSanitizeHtml, removeDivByClass, sanitizeHtml } from '@/src/util/html';

import { ELLIPSIS_CLASS, MESSAGE_HISTORY_TW_SUPPORT_STYLE } from '../../../Config/inbox.config';
import { parseInlineImages } from '../../../Helper';
import { getUserWithEmailDisplay } from '../../../models/InboxEntry';
import { EmailEntry as EmailEntryType } from '../../../types';
import EmailEntryFiles from '../EmailEntryFiles/EmailEntryFiles';

const getEmailMessage = (emailEntryHtml) => {
  const sanitizedHtml = sanitizeHtml(emailEntryHtml);
  const { html, hasDiv } = removeDivByClass(sanitizedHtml, ELLIPSIS_CLASS);

  return {
    parsedMessage: parseAndSanitizeHtml(parseInlineImages(html), { FORBID_TAGS: [] }),
    hasEllipsisContent: hasDiv,
  };
};

type EmailEntryProps = {
  emailEntry: EmailEntryType;
  onRendered: () => void;
  dateFormat?: string;
};

const EmailEntry = ({
  emailEntry,
  onRendered,
  dateFormat = DEFAULT_DATE_TIME_A_FORMAT,
}: EmailEntryProps) => {
  const fromRecipients = emailEntry.from.map((from) => getUserWithEmailDisplay(from)).join(',');
  const toRecipients = emailEntry.to.map((to) => getUserWithEmailDisplay(to)).join(',');
  const ccRecipients = emailEntry.cc.map((to) => getUserWithEmailDisplay(to)).join(',');
  const dateAdded = moment(emailEntry.dateAdded).format(dateFormat);
  const { parsedMessage, hasEllipsisContent } = getEmailMessage(emailEntry.html);

  useEffect(() => {
    onRendered();
  }, [onRendered]);

  return (
    <React.Fragment>
      {/* Header */}
      <table className="printTableContainer">
        <tbody>
          <tr>
            <td width={'100%'} colSpan={2}>
              <h2
                className={clsx(
                  'printSubject--container',
                  'tw-mb-[10px]',
                  // (!) is not removed here as it is to override the styles from vendor.css
                  'tw-text-cfo-black !tw-text-[18px] tw-font-brandonTextBold',
                  'tw-leading-[20px]'
                )}
              >
                {emailEntry.subject}
              </h2>
            </td>
          </tr>
          <tr>
            <td>
              <p className="p-text">
                <strong>{fromRecipients}</strong>
              </p>
            </td>
            <td style={{ textAlign: 'right' }}>
              <p className="p-text">{dateAdded}</p>
            </td>
          </tr>
          <tr>
            <td width={'100%'} colSpan={2}>
              <p className="p-text">To: {toRecipients}</p>
              {ccRecipients && <p className="p-text">Cc: {ccRecipients}</p>}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Content */}
      <div
        className={clsx(
          'parsedEmailContent--container',
          'tw-text-cfo-black',
          'tw-py-[20px] tw-px-0'
        )}
      >
        <ShadowRoot htmlView={parsedMessage} styleTagContent={MESSAGE_HISTORY_TW_SUPPORT_STYLE} />

        {hasEllipsisContent && (
          <span className={clsx('quotedText', 'tw-text-cfo-grey-600')}> [Quoted text hidden] </span>
        )}
      </div>

      {/* Files */}
      {emailEntry.files.list.length > 0 && (
        <EmailEntryFiles emailEntryFiles={emailEntry.files.list} />
      )}
    </React.Fragment>
  );
};

export default EmailEntry;
